import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const THEME_MODES = ['light', 'dark'] as const;

export type ThemeMode = (typeof THEME_MODES)[number];

function isThemeMode(value: string | null): value is ThemeMode {
  return THEME_MODES.some((mode) => mode === value);
}

type ThemeContextValue = {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DEFAULT_STORAGE_KEY = 'ds-theme';
/** Legacy product key — migrated once into ds-theme. */
const LEGACY_STORAGE_KEY = 'theme';

function readStoredTheme(
  storageKey: string,
  defaultTheme: ThemeMode,
): ThemeMode {
  if (typeof window === 'undefined') return defaultTheme;
  const stored = window.localStorage.getItem(storageKey);
  if (isThemeMode(stored)) return stored;

  const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (isThemeMode(legacy)) {
    window.localStorage.setItem(storageKey, legacy);
    return legacy;
  }
  return defaultTheme;
}

function applyTheme(theme: ThemeMode) {
  const root = document.documentElement;
  // Suppress transitions during the swap so every token doesn't animate.
  root.classList.add('**:transition-none!');
  root.classList.toggle('dark', theme === 'dark');
  root.dataset['theme'] = theme;
  root.style.colorScheme = theme;
  window.setTimeout(() => {
    root.classList.remove('**:transition-none!');
  }, 1);
}

/**
 * Class-based dark mode (`html.dark`) — product already uses dark: utilities.
 * Tokens re-map under `.dark` in tokens.css.
 */
export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = DEFAULT_STORAGE_KEY,
}: {
  children: ReactNode;
  defaultTheme?: ThemeMode;
  storageKey?: string;
}) {
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    readStoredTheme(storageKey, defaultTheme),
  );

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  const setTheme = useCallback((t: ThemeMode) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === 'light' ? 'dark' : 'light')),
    [],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
